import { reactive, ref } from "vue";
import { currentService } from "@/views/project-list";
import mime from "mime";

export let fileToUpload = ref();
export let currentDirectory = ref("");
export let uploadGroups: FormData[] = [];
export let serviceFolders: any = {}; // {serviceid: {dirname: {}}}
export let dbServiceFolders: any = {}; // {serviceid: {dirname: {}}}
export let urlCache: { [path: string]: { url: string; expiresAt: number } } = {};

type UploadTarget = 'db' | 'host';
type UploadedItemInfo = {
    file: File;
    relPath: string;       // path relative to currentDir (e.g. "sub/x.png" or "x.png")
    storagePath: string;   // full storage key (currentDir + relPath)
    target: UploadTarget;
    storeKey?: string;
};
type QueueItem = {
    file: File;
    currentDir: string;
    target: UploadTarget;
    foldersRef?: any;
    storeKey?: string;
    contentTypeMapping?: { [fname: string]: string };
    callback?: (hasOverwritten: boolean) => void;
    onItemUploaded?: (info: UploadedItemInfo) => void;
};

// Per-target upload state. The two UI pages (hosting.vue and dbfile.vue)
// each watch their own target's progress/count so a db-file upload never
// bleeds into the hosting view and vice versa.
type TargetState = {
    queue: QueueItem[];
    isUploading: boolean;
    hasOverwrittenFiles: boolean;
    activeCallbacks: Set<(hasOverwritten: boolean) => void>;
    uploadCount: number[]; // [finished, total]
    uploadProgress: any;   // { name, progress, fileSize, target }
};

const makeTargetState = (): TargetState => ({
    queue: [],
    isUploading: false,
    hasOverwrittenFiles: false,
    activeCallbacks: new Set(),
    uploadCount: reactive([0, 0]) as unknown as number[],
    uploadProgress: reactive({}),
});

const targetStates: Record<UploadTarget, TargetState> = {
    host: makeTargetState(),
    db: makeTargetState(),
};

// Hosting-page bindings (unchanged export names for backward compatibility).
export const uploadCount = targetStates.host.uploadCount;
export const uploadProgress = targetStates.host.uploadProgress;

// Db-file-page bindings (separate reactive state).
export const dbUploadCount = targetStates.db.uploadCount;
export const dbUploadProgress = targetStates.db.uploadProgress;

async function processUploadQueue(target: UploadTarget) {
    const state = targetStates[target];
    if (state.isUploading) return;
    state.isUploading = true;

    while (state.queue.length > 0) {
        let item = state.queue.shift()!;
        let f = item.file;
        let currentDir = item.currentDir;

        state.uploadProgress.name = currentDir + (f.webkitRelativePath || f.name);
        state.uploadProgress.progress = 0;
        state.uploadProgress.fileSize = f.size;
        state.uploadProgress.target = target;

        let form = new FormData();
        form.append("files[]", f);

        try {
            await currentService.value
                .uploadHostFiles(form, {
                    contentTypeMapping: item.contentTypeMapping,
                    prefix: currentDir,
                    target,
                    progress: async (e) => {
                        if (e.status !== "progress") {
                            return;
                        }

                        let percent = Math.floor(e.progress);
                        state.uploadProgress.progress = percent;

                        if (percent !== 100) {
                            return;
                        }

                        state.uploadCount[0]++;
                        let fPathArr = (f.webkitRelativePath || f.name).split("/");
                        let flName = fPathArr.pop();
                        let fPath = currentDir + fPathArr.join("/");
                        if (fPath.slice(-1) === "/") {
                            fPath = fPath.slice(0, -1);
                        }

                        // Per-item hook: invoke before folder bookkeeping so the
                        // caller can act on each finished file (e.g. notify an
                        // AI agent) without waiting for the whole batch.
                        if (item.onItemUploaded) {
                            const relPath = f.webkitRelativePath || f.name;
                            const storagePath = currentDir + relPath;
                            try {
                                item.onItemUploaded({
                                    file: f,
                                    relPath,
                                    storagePath,
                                    target,
                                    storeKey: item.storeKey,
                                });
                            } catch (cbErr) {
                                console.error("onItemUploaded callback failed", cbErr);
                            }
                        }

                        let _storeKey = item.storeKey || currentService.value.service.subdomain;
                        let fileObj: any = {
                            name: flName,
                            size: f.size,
                            path:
                                _storeKey +
                                (fPath === "" ? "" : "/" + fPath),
                            upl: new Date().getTime(),
                        };

                        let _foldersRef = item.foldersRef || serviceFolders;
                        let fPathSplit = fPath.split("/");

                        if (fPathSplit.length === 1) {
                            if (fPathSplit[0] === "") {
                                let folder =
                                    _foldersRef[
                                        _storeKey
                                    ]["!"].pager;
                                if (folder.list?.[flName]) {
                                    state.hasOverwrittenFiles = true;
                                    await folder.editItem(fileObj);
                                } else {
                                    await folder.insertItems(fileObj);
                                }
                            } else {
                                let folder =
                                    _foldersRef[
                                        _storeKey
                                    ]["!"].pager;
                                if (!folder.list?.["#" + fPathSplit[0]]) {
                                    let obj = Object.assign({}, fileObj, {
                                        name: "#" + fPathSplit[0],
                                        cnt: 0,
                                        size: 0,
                                        path: _storeKey,
                                    });
                                    await folder.insertItems(obj);
                                } else {
                                    let obj = folder.list?.["#" + fPathSplit[0]];
                                    obj.size = (obj.size || 0) + f.size;
                                    obj.cnt = (obj.cnt || 0) + 1;
                                    await folder.editItem(obj);
                                }

                                if (
                                    _foldersRef[
                                        _storeKey
                                    ][fPathSplit[0]]
                                ) {
                                    let folder =
                                        _foldersRef[
                                            _storeKey
                                        ][fPathSplit[0]].pager;
                                    if (folder.list?.[flName]) {
                                        state.hasOverwrittenFiles = true;
                                        await folder.editItem(fileObj);
                                    } else {
                                        await folder.insertItems(fileObj);
                                    }
                                }
                            }
                        } else {
                            // has nested folders
                            let pathIdx = fPathSplit.length;
                            let fp = fPathSplit.slice(0, pathIdx).join("/");
                            if (
                                _foldersRef[_storeKey][fp]
                            ) {
                                let folder =
                                    _foldersRef[
                                        _storeKey
                                    ][fp].pager;
                                if (folder.list?.[flName]) {
                                    state.hasOverwrittenFiles = true;
                                    await folder.editItem(fileObj);
                                } else {
                                    await folder.insertItems(fileObj);
                                }
                            }
                            do {
                                if (pathIdx) {
                                    fileObj = Object.assign({}, fileObj);
                                    let fp = fPathSplit.slice(0, pathIdx);
                                    let fldName = "#" + fp.pop();
                                    let fpStr = fp.join("/") || "!";
                                    fileObj.name = fldName;
                                    if (
                                        _foldersRef[
                                            _storeKey
                                        ][fpStr]
                                    ) {
                                        let folder =
                                            _foldersRef[
                                                _storeKey
                                            ][fpStr].pager;
                                        if (folder.list?.[fldName]) {
                                            let obj = folder.list?.[fldName];
                                            obj.size = (obj.size || 0) + f.size;
                                            obj.cnt = (obj.cnt || 0) + 1;
                                            obj.path =
                                                _storeKey +
                                                (fpStr.replace("!", "")
                                                    ? "/" + fpStr
                                                    : "");
                                            await folder.editItem(obj);
                                        } else {
                                            fileObj.cnt = 1;
                                            fileObj.size = f.size;
                                            fileObj.path =
                                                _storeKey +
                                                (fpStr.replace("!", "")
                                                    ? "/" + fpStr
                                                    : "");
                                            await folder.insertItems(fileObj);
                                        }
                                    }
                                }
                            } while (pathIdx--);
                        }

                        if (state.uploadCount[0] === state.uploadCount[1] && state.queue.length === 0) {
                            // all files for this target uploaded
                            let callbacks = [...state.activeCallbacks];
                            let overwritten = state.hasOverwrittenFiles;
                            state.uploadCount[0] = 0;
                            state.uploadCount[1] = 0;
                            state.uploadProgress.name = "";
                            state.uploadProgress.progress = 0;
                            state.uploadProgress.fileSize = 0;
                            state.uploadProgress.target = "";
                            state.hasOverwrittenFiles = false;
                            state.activeCallbacks.clear();
                            for (let cb of callbacks) {
                                cb(overwritten);
                            }
                            return true;
                        }
                    },
                });
        } catch (err: any) {
            state.uploadCount[0] = 0;
            state.uploadCount[1] = 0;
            state.uploadProgress.name = "";
            state.uploadProgress.progress = 0;
            state.uploadProgress.fileSize = 0;
            state.uploadProgress.target = "";
            state.queue.length = 0;
            state.hasOverwrittenFiles = false;
            let callbacks = [...state.activeCallbacks];
            state.activeCallbacks.clear();
            for (let cb of callbacks) {
                cb(false);
            }
            // window.alert(err.message);
            state.isUploading = false;
            throw err;
        }
    }

    state.isUploading = false;
}

export let uploadFiles = async (
    files: File[],
    callback?: (hasOverwritten: boolean) => void,
    contentTypeMapping?: { [fname: string]: string },
    options?: {
        target?: UploadTarget;
        currentDir?: string;
        foldersRef?: any;
        storeKey?: string;
        onItemUploaded?: (info: UploadedItemInfo) => void;
    }
) => {
    const target: UploadTarget = options?.target === 'db' ? 'db' : 'host';

    if (target !== 'db') {
        let sd = currentService.value.service.subdomain;
        if (!sd) {
            throw new Error("No subdomain found");
        }
        if ((sd && sd[0] === "*") || sd[0] === "+") {
            throw new Error("Cannot upload files to pending subdomain");
        }
    }

    const state = targetStates[target];
    state.uploadCount[1] += files.length;
    let currentDir = options?.currentDir !== undefined
        ? (options.currentDir ? options.currentDir + "/" : "")
        : (currentDirectory.value ? currentDirectory.value + "/" : "");

    if (callback) {
        state.activeCallbacks.add(callback);
    }

    for (let f of files) {
        state.queue.push({ file: f, currentDir, target, foldersRef: options?.foldersRef, storeKey: options?.storeKey, contentTypeMapping, callback, onItemUploaded: options?.onItemUploaded });
    }

    processUploadQueue(target);
};

let contentTypeMapping: { [filename: string]: string } = {};

function traverseFileTree(item: { [key: string]: any }, path = "") {
    return new Promise((resolve) => {
        if (item.isFile) {
            item.file(function (file: { [key: string]: any }) {
                if (!file.type) {
                    function getExtensionOfFilename(filename: string) {
                        var _fileLen = filename.length;
                        var _lastDot = filename.lastIndexOf(".");
                        var _fileExt = filename
                            .substring(_lastDot, _fileLen)
                            .toLowerCase();
                        return _fileExt;
                    }
                    let type = getExtensionOfFilename(file.name);
                    contentTypeMapping[file.name] = mime.getType(type);
                }
                resolve([{ file, path: path + file.name }]);
            });
        } else if (item.isDirectory) {
            let dirReader = item.createReader();
            dirReader.readEntries(async function (
                entries: { [key: string]: any }[]
            ) {
                let promises = entries.map((entry) =>
                    traverseFileTree(entry, path + item.name + "/")
                );
                let results = await Promise.all(promises);
                resolve([].concat(...results));
            });
        }
    });
}

export let onDrop = async (
    e: any,
    callback?: (hasOverwritten: boolean) => void,
    options?: {
        target?: 'db' | 'host';
        currentDir?: string;
        foldersRef?: any;
        storeKey?: string;
        onItemUploaded?: (info: UploadedItemInfo) => void;
    }
) => {
    e.preventDefault();

    let items = e.dataTransfer.items;
    let formData = new FormData();
    let allFiles = [];
    let filePromises = [];

    for (let i = 0; i < items.length; i++) {
        let item = items[i].webkitGetAsEntry();

        if (item) {
            filePromises.push(traverseFileTree(item));
        }
    }

    let allFileGroups = await Promise.all(filePromises);
    allFiles = [].concat(...allFileGroups);

    allFiles.forEach(({ file, path }) => {
        formData.append("files[]", file, path);
    });

    uploadFiles(
        formData.getAll("files[]") as File[],
        callback,
        contentTypeMapping,
        options
    );
};
